import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Upload, FileText, Car, Cpu } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';
import vehiclesData from '../../data/vehicles.json';
import { ecuTools } from '../../mockData';

const VEHICLE_CATEGORIES = [
  'Voiture', 'Moto', 'Camion / Poids lourd', 'Tracteur agricole',
  'Bateau', 'Jet-ski', 'Engin de chantier', 'Autre'
];
const FUEL_TYPES = ['Diesel', 'Essence', 'Hybride', 'E85 Flexfuel', 'GPL', 'Autre'];
const GEARBOX_TYPES = ['Manuelle', 'Automatique', 'DSG / DCT', 'CVT', 'Autre'];
const TRANSMISSION_TYPES = ['2 roues motrices', '4 roues motrices (4x4)'];
const FILE_TYPES = ['Master', 'Slave'];
const READ_METHODS = ['OBD', 'Bench', 'Boot', 'BDM', 'JTAG'];
const HARDWARE_MODS = [
  'Filtre à air sport', 'Échappement sport', 'Décatalyseur',
  'Intercooler upgrade', 'Turbo upgrade', 'Injecteurs upgrade',
  'FAP/DPF supprimé physiquement', 'EGR bouché physiquement',
  'Catch can', 'Aucune modification'
];

const NewOrderForm = ({ user }) => {
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [options, setOptions] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [selectedBrand, setSelectedBrand] = useState('');
  const [availableModels, setAvailableModels] = useState([]);

  const [orderForm, setOrderForm] = useState({
    category: 'Voiture',
    brand: '',
    model: '',
    year: '',
    engine: '',
    engineCode: '',
    ecuType: '',
    power: '',
    fuel: '',
    gearbox: '',
    transmission: '',
    mileage: '',
    ecuTool: '',
    fileType: '',
    readMethod: '',
    notes: '',
    file: null
  });

  useEffect(() => {
    loadServicesAndOptions();
  }, []);

  const handleBrandChange = (e) => {
    const brandId = e.target.value;
    setSelectedBrand(brandId);
    const brand = vehiclesData.brands.find(b => b.id === brandId);
    if (brand) {
      setAvailableModels(brand.models);
      setOrderForm({ ...orderForm, brand: brand.name, model: '' });
    } else {
      setAvailableModels([]);
      setOrderForm({ ...orderForm, brand: '', model: '' });
    }
  };

  const handleModelChange = (e) => {
    setOrderForm({ ...orderForm, model: e.target.value });
  };

  const filteredBrands = vehiclesData.brands.filter(b => b.id !== 'autre');
  const filteredModels = availableModels.filter(m => m.toLowerCase() !== 'autre');

  const loadServicesAndOptions = async () => {
    try {
      const [servicesRes, optionsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/services`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/options`)
      ]);
      const servicesData = await servicesRes.json();
      const optionsData = await optionsRes.json();
      setServices(servicesData);
      setOptions(optionsData);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les services", variant: "destructive" });
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOrderForm({ ...orderForm, file });
    toast({ title: "Fichier sélectionné", description: `${file.name} prêt à être uploadé` });
  };

  const calculateOrderPrice = () => {
    const selectedServiceObj = services.find(s => s.key === selectedService);
    const servicePrice = selectedServiceObj ? selectedServiceObj.price : 0;
    const optionsPrice = selectedOptions.reduce((total, optKey) => {
      const option = options.find(o => o.key === optKey);
      return total + (option ? option.price : 0);
    }, 0);
    return servicePrice + optionsPrice;
  };

  const getCompatibleOptions = () => {
    if (!selectedService) return [];
    return options.filter(opt =>
      opt.compatible_services.length === 0 ||
      opt.compatible_services.includes(selectedService)
    );
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();

    if (!selectedService) {
      toast({ title: "Erreur", description: "Veuillez sélectionner un service", variant: "destructive" });
      return;
    }
    if (!orderForm.file) {
      toast({ title: "Erreur", description: "Veuillez uploader le fichier ECU original", variant: "destructive" });
      return;
    }
    if (!orderForm.ecuTool) {
      toast({ title: "Erreur", description: "Veuillez sélectionner votre outil ECU", variant: "destructive" });
      return;
    }

    setUploadingFile(true);

    try {
      const { ordersAPI, paymentAPI } = await import('../../api');

      const orderData = {
        vehicle_info: {
          category: orderForm.category,
          brand: orderForm.brand,
          model: orderForm.model,
          year: orderForm.year,
          engine: orderForm.engine,
          engine_code: orderForm.engineCode,
          ecu_type: orderForm.ecuType,
          power: orderForm.power,
          fuel: orderForm.fuel,
          gearbox: orderForm.gearbox,
          transmission: orderForm.transmission,
          mileage: orderForm.mileage,
          ecu_tool: orderForm.ecuTool,
          file_type: orderForm.fileType,
          read_method: orderForm.readMethod
        },
        service: selectedService,
        options: selectedOptions,
        notes: orderForm.notes || ''
      };

      const order = await ordersAPI.createOrder(orderData);
      await ordersAPI.uploadOriginalFile(order.order_id, orderForm.file);
      const checkoutSession = await paymentAPI.createCheckoutSession(order.order_id);

      toast({
        title: "Redirection vers le paiement...",
        description: "Vous allez être redirigé vers la page de paiement sécurisée"
      });
      window.location.href = checkoutSession.checkout_url;
    } catch (error) {
      console.error('Order creation error:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || error.message || "Impossible de créer la commande",
        variant: "destructive"
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const inputCls = "w-full px-3 py-2 bg-[#111111] border border-[#A6A6A6]/30 text-white rounded-md focus:border-[#C00000] focus:outline-none";

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">
              <span className="text-[#C00000]">Nouvelle Demande Tuning</span>
            </h1>
            <p className="text-[#A6A6A6]">Commandez un fichier de reprogrammation ECU</p>
          </div>

          <form onSubmit={handleCreateOrder} className="space-y-6" data-testid="new-order-form">

            {/* SECTION 1 — VÉHICULE */}
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Car className="h-5 w-5 text-[#C00000]" /> Informations du véhicule
                </CardTitle>
                <CardDescription className="text-[#A6A6A6]">
                  Renseignez avec précision les caractéristiques du véhicule
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-[#A6A6A6]">Catégorie de véhicule *</Label>
                  <select
                    value={orderForm.category}
                    onChange={(e) => setOrderForm({ ...orderForm, category: e.target.value })}
                    className={inputCls}
                    data-testid="vehicle-category-select"
                    required
                  >
                    {VEHICLE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Marque (dropdown only) */}
                <div className="space-y-2">
                  <Label htmlFor="brand" className="text-[#A6A6A6]">Marque *</Label>
                  <select
                    id="brand"
                    value={selectedBrand}
                    onChange={handleBrandChange}
                    className={inputCls}
                    data-testid="brand-select"
                    required
                  >
                    <option value="">Sélectionnez une marque</option>
                    {filteredBrands.map((brand) => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </select>
                </div>

                {/* Modèle (dropdown only) */}
                <div className="space-y-2">
                  <Label htmlFor="model" className="text-[#A6A6A6]">Modèle *</Label>
                  <select
                    id="model"
                    value={orderForm.model}
                    onChange={handleModelChange}
                    className={inputCls}
                    data-testid="model-select"
                    required
                    disabled={!selectedBrand}
                  >
                    <option value="">
                      {selectedBrand ? "Sélectionnez un modèle" : "Sélectionnez d'abord une marque"}
                    </option>
                    {filteredModels.map((model) => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Année *</Label>
                    <Input
                      value={orderForm.year}
                      onChange={(e) => setOrderForm({ ...orderForm, year: e.target.value })}
                      placeholder="2018"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      data-testid="year-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Kilométrage</Label>
                    <Input
                      value={orderForm.mileage}
                      onChange={(e) => setOrderForm({ ...orderForm, mileage: e.target.value })}
                      placeholder="120 000 km"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      data-testid="mileage-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Moteur</Label>
                    <Input
                      value={orderForm.engine}
                      onChange={(e) => setOrderForm({ ...orderForm, engine: e.target.value })}
                      placeholder="2.0 TDI"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      data-testid="engine-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="engineCode" className="text-[#A6A6A6]">Code moteur *</Label>
                    <Input
                      id="engineCode"
                      value={orderForm.engineCode}
                      onChange={(e) => setOrderForm({ ...orderForm, engineCode: e.target.value })}
                      placeholder="CUNA"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      data-testid="engine-code-input"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Puissance (cv)</Label>
                    <Input
                      value={orderForm.power}
                      onChange={(e) => setOrderForm({ ...orderForm, power: e.target.value })}
                      placeholder="150"
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      data-testid="power-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Carburant</Label>
                    <select
                      value={orderForm.fuel}
                      onChange={(e) => setOrderForm({ ...orderForm, fuel: e.target.value })}
                      className={inputCls}
                      data-testid="fuel-select"
                    >
                      <option value="">Choisir...</option>
                      {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Boîte de vitesses</Label>
                    <select
                      value={orderForm.gearbox}
                      onChange={(e) => setOrderForm({ ...orderForm, gearbox: e.target.value })}
                      className={inputCls}
                      data-testid="gearbox-select"
                    >
                      <option value="">Choisir...</option>
                      {GEARBOX_TYPES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A6A6A6]">Transmission</Label>
                  <select
                    value={orderForm.transmission}
                    onChange={(e) => setOrderForm({ ...orderForm, transmission: e.target.value })}
                    className={inputCls}
                    data-testid="transmission-select"
                  >
                    <option value="">Choisir...</option>
                    {TRANSMISSION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* SECTION 2 — ECU & OUTIL */}
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Cpu className="h-5 w-5 text-[#C00000]" /> Calculateur & Outil de reprogrammation
                </CardTitle>
                <CardDescription className="text-[#A6A6A6]">
                  Indications nécessaires pour traiter le fichier correctement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="ecuType" className="text-[#A6A6A6]">Type ECU / Calculateur *</Label>
                  <Input
                    id="ecuType"
                    value={orderForm.ecuType}
                    onChange={(e) => setOrderForm({ ...orderForm, ecuType: e.target.value })}
                    placeholder="Bosch MED17, Siemens SID208, Continental..."
                    className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                    data-testid="ecu-type-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A6A6A6]">Outil de lecture/écriture ECU utilisé *</Label>
                  <select
                    value={orderForm.ecuTool}
                    onChange={(e) => setOrderForm({ ...orderForm, ecuTool: e.target.value })}
                    className={inputCls}
                    data-testid="ecu-tool-select"
                    required
                  >
                    <option value="">Sélectionnez votre outil</option>
                    {ecuTools.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Type de fichier</Label>
                    <select
                      value={orderForm.fileType}
                      onChange={(e) => setOrderForm({ ...orderForm, fileType: e.target.value })}
                      className={inputCls}
                      data-testid="file-type-select"
                    >
                      <option value="">Choisir...</option>
                      {FILE_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Méthode de lecture</Label>
                    <select
                      value={orderForm.readMethod}
                      onChange={(e) => setOrderForm({ ...orderForm, readMethod: e.target.value })}
                      className={inputCls}
                      data-testid="read-method-select"
                    >
                      <option value="">Choisir...</option>
                      {READ_METHODS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SECTION 3 — SERVICE & OPTIONS */}
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Service & Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="service" className="text-[#A6A6A6]">Service demandé *</Label>
                  <select
                    id="service"
                    value={selectedService}
                    onChange={(e) => { setSelectedService(e.target.value); setSelectedOptions([]); }}
                    className={inputCls}
                    data-testid="service-select"
                    required
                  >
                    <option value="">Sélectionnez un service</option>
                    {services.map((service) => (
                      <option key={service.key} value={service.key}>
                        {service.name} - {service.price}€ HT
                      </option>
                    ))}
                  </select>
                  {selectedService && (
                    <p className="text-xs text-[#A6A6A6] mt-1">
                      {services.find(s => s.key === selectedService)?.description}
                    </p>
                  )}
                </div>

                {selectedService && getCompatibleOptions().length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-[#A6A6A6]">Options additionnelles</Label>
                    <div className="space-y-2">
                      {getCompatibleOptions().map((option) => (
                        <label key={option.key} className="flex items-center gap-3 p-3 bg-[#111111] border border-[#A6A6A6]/30 rounded-md cursor-pointer hover:border-[#C00000]/50 transition">
                          <input
                            type="checkbox"
                            checked={selectedOptions.includes(option.key)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedOptions([...selectedOptions, option.key]);
                              else setSelectedOptions(selectedOptions.filter(k => k !== option.key));
                            }}
                            className="w-4 h-4 accent-[#C00000]"
                            data-testid={`option-${option.key}`}
                          />
                          <div className="flex-1">
                            <span className="text-white font-semibold">{option.name}</span>
                            <p className="text-xs text-[#A6A6A6]">{option.description}</p>
                          </div>
                          <span className="text-[#C00000] font-bold">+{option.price}€</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SECTION 5 — FICHIER & NOTES */}
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Fichier ECU & informations complémentaires</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="file" className="text-[#A6A6A6]">Fichier ECU original *</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="file"
                      type="file"
                      onChange={handleFileUpload}
                      className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      accept=".bin,.hex,.kess,.ori,.frf,.bdm"
                      data-testid="file-input"
                      required
                    />
                    <Upload className="h-5 w-5 text-[#C00000]" />
                  </div>
                  <p className="text-xs text-[#A6A6A6]">Formats acceptés : .bin, .hex, .kess, .ori, .frf, .bdm</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-[#A6A6A6]">Notes / Demandes spécifiques</Label>
                  <textarea
                    id="notes"
                    rows="4"
                    value={orderForm.notes}
                    onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                    placeholder="Informations supplémentaires, défauts moteur, demandes spécifiques..."
                    className={`${inputCls} resize-none`}
                    data-testid="notes-textarea"
                  ></textarea>
                </div>
              </CardContent>
            </Card>

            {/* RÉCAP & VALIDATION */}
            <Card className="bg-[#1a1a1a] border-[#C00000]/30">
              <CardContent className="pt-6">
                <div className="bg-[#C00000]/10 border border-[#C00000]/30 rounded-lg p-4 mb-4">
                  <p className="text-white font-semibold mb-2">Résumé de la commande</p>
                  {selectedService ? (
                    <>
                      <div className="space-y-1 mb-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A6A6A6]">{services.find(s => s.key === selectedService)?.name}</span>
                          <span className="text-white">{services.find(s => s.key === selectedService)?.price}€</span>
                        </div>
                        {selectedOptions.map(optKey => {
                          const option = options.find(o => o.key === optKey);
                          return option ? (
                            <div key={optKey} className="flex justify-between text-sm">
                              <span className="text-[#A6A6A6]">+ {option.name}</span>
                              <span className="text-white">{option.price}€</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                      <div className="flex justify-between items-center border-t border-[#C00000]/30 pt-3">
                        <span className="text-[#A6A6A6]">Total</span>
                        <span className="text-2xl font-bold text-[#C00000]" data-testid="order-total">
                          {calculateOrderPrice().toFixed(2)}€ HT
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-[#A6A6A6]">Prix estimé</span>
                      <span className="text-2xl font-bold text-[#C00000]">--€ HT</span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={uploadingFile}
                  data-testid="submit-order-btn"
                  className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white font-semibold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed">
                  <FileText className="h-5 w-5 mr-2" />
                  {uploadingFile ? 'Création en cours...' : 'Commander maintenant'}
                </Button>
              </CardContent>
            </Card>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewOrderForm;
